import React from 'react';
import { Button, ButtonProps as AntButtonProps, TooltipProps } from 'antd';
import styled, { css } from 'styled-components';
import type { ThemeTokens } from '@ui-library/tokens/types';
import { nextVariant } from '@ui-library/styles';

type ExtendedSizeType = AntButtonProps['size'] | 'xlarge';
type ExtendedType = AntButtonProps['type'] | 'secondary';

type NextLevel = 'primary' | 'secondary' | 'tertiary';
type NextIntent = 'default' | 'destructive';

export interface ButtonProps extends Omit<AntButtonProps, 'size' | 'type'> {
  type?: ExtendedType;
  size?: ExtendedSizeType;
  variant?: Variant | 'next';
  level?: NextLevel;
  intent?: NextIntent;
  tooltip?: TooltipProps & { show?: boolean };
  noPadding?: boolean;
}

const antSizeToTokenSize = {
  large: 'fontSize',
  middle: 'fontSize',
  small: 'fontSizeSM',
  xlarge: 'fontSizeLG',
} as const satisfies Record<Exclude<ExtendedSizeType, undefined>, string>;

const getDefaultTypeColors = (t: ThemeTokens) => ({
  default: {
    border: t.colorBorder,
    color: t.colorText,
    hoverBackground: t.colorBgTextHover,
    activeBackground: t.colorBgTextActive,
    outlineColor: t.colorBorder,
  },
  danger: {
    border: t.colorErrorBorder,
    color: t.colorError,
    hoverBackground: t.colorErrorBg,
    activeBackground: t.colorErrorBgHover,
    outlineColor: t.colorErrorBorder,
  },
  confirm: {
    border: t.colorSuccessBorder,
    color: t.colorSuccess,
    hoverBackground: t.colorSuccessBg,
    activeBackground: t.colorSuccessBgHover,
    outlineColor: t.colorSuccessBorder,
  },
});

export type Variant = keyof ReturnType<typeof getDefaultTypeColors>;
export type CustomSize = keyof typeof antSizeToTokenSize;

/** Narrows the full variant union to the old color-intent type (safe — 'next' is handled upstream). */
const asOldVariant = (v: ButtonProps['variant']): Variant =>
  v === 'next' || v === undefined ? 'default' : v;

const mapSizeToWeight = {
  large: 600,
  middle: 500,
  small: 400,
  xlarge: 600,
} as const;

const commonButtonStyles = css<ButtonProps>`
  font-weight: ${({ size = 'middle' }) => mapSizeToWeight[size]};

  &:disabled {
    /* prevent layout shift with an invisible border */
    border-color: rgba(0, 0, 0, 0);
    /* Achieves the same effect without opacity, and works correctly on dark backgrounds */
    background-color: ${({ theme }) => theme.token['gray.4']};
  }

  &:not(:disabled):focus {
    outline-offset: 2px !important;
    outline-width: 2px !important;
  }
`;

export const DefaultButton = styled((props: ButtonProps) => {
  const { variant, size, type, ...rest } = props;
  return <Button type="default" size={size === 'xlarge' ? 'large' : size} {...rest} />;
})<ButtonProps>`
  ${commonButtonStyles}

  border-color: ${({ variant, theme }) =>
    getDefaultTypeColors(theme.token)[asOldVariant(variant)].border};
  color: ${({ variant, theme }) => getDefaultTypeColors(theme.token)[asOldVariant(variant)].color};

  &:hover:enabled {
    border-color: ${({ variant, theme }) =>
      getDefaultTypeColors(theme.token)[asOldVariant(variant)].border} !important;
    color: ${({ variant, theme }) =>
      getDefaultTypeColors(theme.token)[asOldVariant(variant)].color} !important;
    background: ${({ variant, theme }) =>
      getDefaultTypeColors(theme.token)[asOldVariant(variant)].hoverBackground} !important;
  }

  &:active:enabled {
    border-color: ${({ variant, theme }) =>
      getDefaultTypeColors(theme.token)[asOldVariant(variant)].border} !important;
    color: ${({ variant, theme }) =>
      getDefaultTypeColors(theme.token)[asOldVariant(variant)].color} !important;
    background: ${({ variant, theme }) =>
      getDefaultTypeColors(theme.token)[asOldVariant(variant)].activeBackground} !important;
  }

  &focus:enabled {
    outline-color: ${({ variant, theme }) =>
      getDefaultTypeColors(theme.token)[asOldVariant(variant)].outlineColor} !important;
  }
`;

const getPrimaryButtonColors = (t: ThemeTokens) => ({
  default: {
    background: t.colorPrimaryBase,
    hoverBackground: t.colorPrimaryHover,
    activeBackground: t.colorPrimaryActive,
    outlineColor: t.colorPrimaryBorder,
  },
  danger: {
    background: t.colorErrorBase,
    hoverBackground: t.colorErrorHover,
    activeBackground: t.colorErrorActive,
    outlineColor: t.colorErrorBorder,
  },
  confirm: {
    background: t.colorSuccessVariant,
    hoverBackground: t.colorSuccessHover,
    activeBackground: t.colorSuccessActive,
    outlineColor: t.colorSuccessBorder,
  },
});

export const PrimaryButton = styled((props: ButtonProps) => {
  const { variant, size, type, ...rest } = props;
  return <Button type="primary" size={size === 'xlarge' ? 'large' : size} {...rest} />;
})<ButtonProps>`
  ${commonButtonStyles}

  background: ${({ variant, theme }) =>
    getPrimaryButtonColors(theme.token)[asOldVariant(variant)].background};

  &:hover:enabled {
    background: ${({ variant, theme }) =>
      getPrimaryButtonColors(theme.token)[asOldVariant(variant)].hoverBackground} !important;
  }

  &:active:enabled {
    background: ${({ variant, theme }) =>
      getPrimaryButtonColors(theme.token)[asOldVariant(variant)].activeBackground} !important;
  }

  &:focus:enabled {
    outline-color: ${({ variant, theme }) =>
      getPrimaryButtonColors(theme.token)[asOldVariant(variant)].outlineColor} !important;
  }
`;

const getSecondaryTypeColors = (t: ThemeTokens) => ({
  default: {
    color: t.colorPrimaryText,
    background: t.colorPrimarySubdued,
    hoverBackground: t.colorPrimarySubduedHover,
    activeBackground: t.colorPrimarySubduedActive,
    outlineColor: t.colorPrimaryBorder,
  },
  danger: {
    color: t.colorErrorText,
    background: t.colorErrorSubdued,
    hoverBackground: t.colorErrorSubduedHover,
    activeBackground: t.colorErrorSubduedActive,
    outlineColor: t.colorErrorBorder,
  },
  confirm: {
    color: t.colorSuccessText,
    background: t.colorSuccessSubdued,
    hoverBackground: t.colorSuccessSubduedHover,
    activeBackground: t.colorSuccessSubduedActive,
    outlineColor: t.colorSuccessBorder,
  },
});

export const SecondaryButton = styled((props: ButtonProps) => {
  const { variant, size, type, ...rest } = props;
  return <Button size={size === 'xlarge' ? 'large' : size} {...rest} />;
})<ButtonProps>`
  ${commonButtonStyles}

  color: ${({ variant, theme }) =>
    getSecondaryTypeColors(theme.token)[asOldVariant(variant)].color};

  /* prevent layout shift with an invisible border */
  border-color: rgba(0, 0, 0, 0) !important;

  background: ${({ variant, theme }) =>
    getSecondaryTypeColors(theme.token)[asOldVariant(variant)].background};

  &:hover:enabled {
    background: ${({ variant, theme }) =>
      getSecondaryTypeColors(theme.token)[asOldVariant(variant)].hoverBackground} !important;
    color: ${({ variant, theme }) =>
      getSecondaryTypeColors(theme.token)[asOldVariant(variant)].color} !important;
  }

  &:active:enabled {
    color: ${({ variant, theme }) =>
      getSecondaryTypeColors(theme.token)[asOldVariant(variant)].color} !important;
    background: ${({ variant, theme }) =>
      getSecondaryTypeColors(theme.token)[asOldVariant(variant)].activeBackground} !important;
  }

  &:focus:enabled {
    outline-color: ${({ variant, theme }) =>
      getSecondaryTypeColors(theme.token)[asOldVariant(variant)].outlineColor} !important;
  }
`;

export const LinkButton = styled((props: ButtonProps) => {
  const { variant, size, type, ...rest } = props;
  return <Button type={type !== 'link' && type !== 'text' ? 'link' : type} {...rest} />;
})<ButtonProps>`
  ${({ noPadding }) => noPadding && 'padding-inline: 0 !important'};

  font-weight: ${({ size = 'middle' }) => mapSizeToWeight[size]};

  font-size: ${({ size = 'middle', theme }) =>
    `${theme.token[antSizeToTokenSize[size]]}px !important`};

  line-height: ${({ size = 'middle', theme }) => `${theme.token[antSizeToTokenSize[size]]}px`};

  > *:hover:enabled,
  > *:focus:enabled {
    cursor: pointer;
    text-decoration: underline;
    outline: none;
  }

  &focus-visible:enabled {
    outline: 0;
    outline-offset: 0;
  }
`;

/* ─── Next variant ─────────────────────────────────────────────────── */

const getNextButtonColors = (n: ThemeTokens['next']) => ({
  'primary-default': {
    bg: n.colorBorderFocus,
    text: n.palette.grey0,
    border: 'transparent',
    hoverBg: n.colorBorderFocus,
    hoverBorder: 'transparent',
    hoverShadow: n.boxShadowFocus,
    pressedBg: n.colorBorderFocus,
    pressedOverlay: 'rgba(0, 0, 0, 0.12)' as string | undefined,
    pressedShadow: n.boxShadowFocus,
    disabledBg: n.palette.grey200,
    disabledText: n.palette.grey25,
    disabledBorder: 'transparent',
  },
  'primary-destructive': {
    bg: n.colorError,
    text: n.palette.grey0,
    border: 'transparent',
    hoverBg: n.colorError,
    hoverBorder: 'transparent',
    hoverShadow: n.boxShadowError,
    pressedBg: n.colorError,
    pressedOverlay: 'rgba(0, 0, 0, 0.12)' as string | undefined,
    pressedShadow: n.boxShadowError,
    disabledBg: n.palette.grey200,
    disabledText: n.palette.grey25,
    disabledBorder: 'transparent',
  },
  'secondary-default': {
    bg: n.palette.grey0,
    text: n.colorText,
    border: n.palette.grey100,
    hoverBg: n.palette.grey0,
    hoverBorder: n.palette.grey200,
    hoverShadow: n.boxShadowHover,
    pressedBg: n.palette.grey25,
    pressedOverlay: undefined as string | undefined,
    pressedShadow: n.boxShadowHover,
    disabledBg: n.palette.grey0,
    disabledText: n.palette.grey200,
    disabledBorder: n.palette.grey100,
  },
  'secondary-destructive': {
    bg: n.palette.grey0,
    text: n.colorError,
    border: n.palette.grey100,
    hoverBg: n.palette.grey0,
    hoverBorder: n.colorError,
    hoverShadow: n.boxShadowError,
    pressedBg: n.palette.red50,
    pressedOverlay: undefined as string | undefined,
    pressedShadow: n.boxShadowError,
    disabledBg: n.palette.grey0,
    disabledText: n.palette.grey200,
    disabledBorder: n.palette.grey100,
  },
  'tertiary-default': {
    bg: 'transparent',
    text: n.colorText,
    border: 'transparent',
    hoverBg: n.palette.grey50,
    hoverBorder: 'transparent',
    hoverShadow: 'none',
    pressedBg: n.palette.grey75,
    pressedOverlay: undefined as string | undefined,
    pressedShadow: 'none',
    disabledBg: 'transparent',
    disabledText: n.palette.grey200,
    disabledBorder: 'transparent',
  },
  'tertiary-destructive': {
    bg: 'transparent',
    text: n.colorError,
    border: 'transparent',
    hoverBg: n.palette.red50,
    hoverBorder: 'transparent',
    hoverShadow: 'none',
    pressedBg: n.palette.red50,
    pressedOverlay: undefined as string | undefined,
    pressedShadow: 'none',
    disabledBg: 'transparent',
    disabledText: n.palette.grey200,
    disabledBorder: 'transparent',
  },
});

type NextColorKey = `${NextLevel}-${NextIntent}`;

interface NextButtonInnerProps extends AntButtonProps {
  $level: NextLevel;
  $intent: NextIntent;
}

const nextButtonMinimalStyles = css<NextButtonInnerProps>`
  ${({ $level, $intent, theme }) => {
    const key: NextColorKey = `${$level}-${$intent}`;
    const colors = getNextButtonColors(theme.token.next);
    const c = colors[key];

    return css`
      &.ant-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: ${c.bg} !important;
        color: ${c.text} !important;
        border: 1px solid ${c.border} !important;
        box-shadow: none;
        font-weight: ${theme.token.next.fontWeight500};
        gap: 4px;

        /* hover */
        &:hover:not(:disabled):not(:active) {
          background: ${c.hoverBg} !important;
          border-color: ${c.hoverBorder} !important;
          box-shadow: ${c.hoverShadow} !important;
          color: ${c.text} !important;
        }

        /* pressed / active */
        &:active:not(:disabled) {
          background: ${c.pressedBg} !important;
          ${c.pressedOverlay
            ? `background: linear-gradient(${c.pressedOverlay}, ${c.pressedOverlay}), ${c.pressedBg} !important;`
            : ''}
          border-color: ${c.hoverBorder} !important;
          box-shadow: ${c.pressedShadow} !important;
          color: ${c.text} !important;
        }

        /* keyboard focus */
        &:focus-visible:not(:disabled) {
          ${nextVariant.focusRing}
          outline: none !important;
        }

        /* disabled */
        &:disabled {
          background: ${c.disabledBg} !important;
          color: ${c.disabledText} !important;
          border-color: ${c.disabledBorder} !important;
          box-shadow: none !important;
          cursor: not-allowed;
        }
      }
    `;
  }}
`;

export const NextButton = styled((props: NextButtonInnerProps) => {
  const { $level, $intent, ...rest } = props;
  return <Button {...rest} />;
})<NextButtonInnerProps>`
  ${nextButtonMinimalStyles}

  &.ant-btn:not(.ant-btn-icon-only) > .ant-btn-icon:not(:last-child) {
    margin-inline-end: 0;
  }
`;
